import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SentimentBadge from "./SentimentBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useGetAiAgentCallQuery } from "@/store/rest_api";
import { AiAgentCall, CallSentiment, CallSummary, parseCallSummary, SquadMemberStat } from "@/model/interface/AiAgent";
import React, { useEffect, useState } from "react";
import TranscriptEntryView, { TranscriptEntry } from "./TranscriptEntryView";
import {
  CheckCircle2,
  ArrowRight,
  ClipboardList,
  Clock,
  Loader2,
  Split,
  UserRound,
} from "lucide-react";

interface TranscriptDialogProps {
  agentId: string;
  callId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function parseTranscript(raw: string | null | undefined): TranscriptEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TranscriptEntry[]) : [];
  } catch {
    return [];
  }
}

function formatDuration(seconds?: number) {
  if (seconds == null) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ summary }: { summary: CallSummary }) {
  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm font-medium leading-snug flex-1">{summary.callSummary}</p>
        <SentimentBadge sentiment={summary.sentiment} />
      </div>

      {summary.outcome && (
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">Outcome — </span>
          {summary.outcome}
        </p>
      )}

      {(summary.keyPoints?.length > 0 || summary.nextSteps?.length > 0) && (
        <div className="grid gap-3 sm:grid-cols-2 pt-1 border-t border-border/50">
          {summary.keyPoints?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-blue-400" /> Key points
              </p>
              <ul className="space-y-1">
                {summary.keyPoints.map((pt, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex gap-1.5 leading-snug">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/50 shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.nextSteps?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <ArrowRight className="h-3 w-3 text-amber-400" /> Next steps
              </p>
              <ul className="space-y-1">
                {summary.nextSteps.map((step, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex gap-1.5 leading-snug">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/50 shrink-0" />
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Details captured ─────────────────────────────────────────────────────────

/**
 * Field order and wording for what the agent gathered. Ordered rather than alphabetical so the
 * card reads the way a person would ask it, with the free-text notes last.
 */
const DETAIL_FIELDS: [key: string, label: string][] = [
  ["motivation", "Motivation"],
  ["timeline", "Timeline"],
  ["condition", "Condition"],
  ["occupancy", "Occupancy"],
  ["decision_makers", "Decision makers"],
  ["notes", "Notes"],
];

/** Turns an unmapped key into something readable, e.g. budget_range → "Budget range". */
function humanizeKey(key: string): string {
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function DetailsCaptured({ data }: { data: Record<string, string> }) {
  const known = DETAIL_FIELDS.filter(([key]) => data[key]?.trim());
  // Fields a newer tool may write that DETAIL_FIELDS doesn't know about yet. These are the
  // caller's own answers, so showing them is right — they just sort after the curated ones.
  const extra = Object.keys(data)
    .filter((key) => !DETAIL_FIELDS.some(([known]) => known === key) && String(data[key] ?? "").trim())
    .map((key) => [key, humanizeKey(key)] as [string, string]);

  const rows = [...known, ...extra];
  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <ClipboardList className="h-3 w-3 text-emerald-400" /> Details captured
      </p>
      <dl className="grid gap-2 sm:grid-cols-[10rem_1fr]">
        {rows.map(([key, label]) => (
          <React.Fragment key={key}>
            <dt className="text-xs font-medium text-foreground">{label}</dt>
            <dd className="text-xs text-muted-foreground leading-snug whitespace-pre-wrap">
              {data[key]}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

// ─── How the call was handled ─────────────────────────────────────────────────

/**
 * Vapi squad calls only: which assistants handled which part, and how long the caller waited each
 * time one handed over to another.
 *
 * A stalled handoff is the failure worth seeing — the caller sat in silence long enough to ask
 * "are you still there?" — so it gets a colour rather than a number to squint at.
 */
function CallHandling({ stats }: { stats: NonNullable<AiAgentCall["orchestratorDiagnostics"]> }) {
  const members = stats.members ?? [];
  if (members.length < 2) return null; // one assistant handled everything: nothing to attribute

  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <Split className="h-3 w-3 text-sky-400" /> How the call was handled
        {stats.stalledHandoffs ? (
          <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
            {stats.stalledHandoffs} slow handover{stats.stalledHandoffs > 1 ? "s" : ""}
          </span>
        ) : null}
      </p>

      <ul className="space-y-1.5">
        {members.map((m: SquadMemberStat) => (
          <li key={m.name} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
            <span className="font-medium text-foreground">{humanizeKey(m.name.replace(/-/g, " "))}</span>
            <span className="text-muted-foreground">
              {m.turns} turn{m.turns === 1 ? "" : "s"}
            </span>
            {m.takeoverMs != null && (
              <span className={m.stalled ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}>
                · picked up after {(m.takeoverMs / 1000).toFixed(1)}s{m.stalled ? " — caller spoke first" : ""}
              </span>
            )}
            {m.tools.length > 0 && (
              <span className="text-muted-foreground/80">· {m.tools.join(", ")}</span>
            )}
          </li>
        ))}
      </ul>

      {stats.neverSpoke && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Handed over to {humanizeKey(stats.neverSpoke.replace(/-/g, " "))}, which never spoke.
        </p>
      )}
    </div>
  );
}

/**
 * Shown only when a person took the call over. `escalatedAt` can be present without a name if the
 * user record has since gone, so the two are rendered independently rather than as a single string.
 */
function EscalationNote({ name, at }: { name?: string; at?: string }) {
  if (!name && !at) return null;
  const when = at ? new Date(at) : null;
  const time = when && !Number.isNaN(when.getTime())
    ? when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 flex items-center gap-2">
      <UserRound className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
      <p className="text-xs text-amber-700 dark:text-amber-400">
        Taken over by {name ?? "a team member"}
        {time && <span className="opacity-75"> at {time}</span>}
      </p>
    </div>
  );
}

function SummaryPending() {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-dashed border-muted-foreground/25 px-4 py-3 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
      Generating call summary…
    </div>
  );
}

function TranscriptSkeleton() {
  return (
    <div className="space-y-3 pt-1">
      <Skeleton className="h-10 w-2/3" />
      <Skeleton className="h-10 w-1/2 ml-auto" />
      <Skeleton className="h-10 w-3/5" />
    </div>
  );
}

function EmptyTranscript() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-sm text-muted-foreground">No transcript available for this call.</p>
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export default function TranscriptDialog({ agentId, callId, open, onOpenChange }: TranscriptDialogProps) {
  const [pollingActive, setPollingActive] = useState(false);

  const { data: call, isLoading } = useGetAiAgentCallQuery(
    { agentId, callId: callId! },
    { skip: !callId || !open, pollingInterval: pollingActive ? 5000 : 0 },
  );

  const summaryPending = (() => {
    if (!call || call.summary) return false;
    const endedAt = call.endedAt ? new Date(call.endedAt).getTime() : 0;
    return Date.now() - endedAt < 5 * 60 * 1000;
  })();

  useEffect(() => { setPollingActive(summaryPending); }, [summaryPending]);
  useEffect(() => { if (!open) setPollingActive(false); }, [open]);

  const summary = parseCallSummary(call?.summary);
  const entries = parseTranscript(call?.transcript);
  const duration = formatDuration(call?.durationSeconds);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">

        <DialogHeader className="shrink-0">
          <DialogTitle className="text-base">Call Details</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center flex-wrap gap-2 mt-1">
              {call?.outcome && (
                <Badge variant="outline" className="text-[11px]">
                  {call.outcome.replace(/_/g, " ")}
                </Badge>
              )}
              {call?.sentiment && <SentimentBadge sentiment={call.sentiment as CallSentiment} />}
              {duration && (
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {duration}
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-3 py-2">
          {/* Summary */}
          {call && !isLoading && (
            summary ? <SummaryCard summary={summary} /> : summaryPending ? <SummaryPending /> : null
          )}

          {/* Who took the call over, if anyone did */}
          {call && !isLoading && (
            <EscalationNote name={call.escalatedToName} at={call.escalatedAt} />
          )}

          {/* What the agent gathered — above the transcript, since it's the actionable part */}
          {call?.collectedData && !isLoading && <DetailsCaptured data={call.collectedData} />}

          {/* Squad shape, when the orchestrator reported one — diagnostic, so it sits below what
              the agent actually gathered. */}
          {call?.orchestratorDiagnostics && !isLoading && (
            <CallHandling stats={call.orchestratorDiagnostics} />
          )}

          {/* Transcript */}
          {isLoading && <TranscriptSkeleton />}
          {!isLoading && entries.length === 0 && <EmptyTranscript />}
          {!isLoading && entries.length > 0 && (
            <div className="space-y-1.5">
              {entries.map((entry, i) => (
                <TranscriptEntryView key={i} entry={entry} />
              ))}
            </div>
          )}
        </div>

        {/* No Take Over here: this dialog shows calls that have already ended. Taking over a live
            call belongs to LiveCallCard, which owns the conference join. */}

      </DialogContent>
    </Dialog>
  );
}
